/**
 * Webhook Management
 * Admin panel for managing incoming webhook endpoints
 */

import { useState } from 'react';
import { Plus, Trash2, Copy, ChevronDown, ChevronRight, Power, PowerOff, Loader2, Webhook, Check } from 'lucide-react';
import { useWebhooks, useCreateWebhook, useUpdateWebhook, useDeleteWebhook, useWebhookLogs } from '@/hooks/useWebhooks';
import { useAssistants } from '@/hooks/useAssistants';
import { useChannels } from '@/hooks/useChannels';
import type { WebhookEndpoint } from '@/types/api.types';
import { toast } from 'sonner';
import { asApiError } from '@/utils/error.util';

const API_BASE = import.meta.env.VITE_API_URL || '';

export const WebhookManagement = () => {
  const { data: webhooks, isLoading } = useWebhooks();
  const { data: bots } = useAssistants();
  const { data: channels } = useChannels();
  const createMutation = useCreateWebhook();
  const updateMutation = useUpdateWebhook();
  const deleteMutation = useDeleteWebhook();

  const [showCreate, setShowCreate] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Create form state
  const [newName, setNewName] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [newChannelId, setNewChannelId] = useState('');
  const [newBotUserId, setNewBotUserId] = useState('');
  const [newTemplate, setNewTemplate] = useState('');
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName || !newSlug || !newChannelId || !newBotUserId) {
      toast.error('Всички задължителни полета трябва да бъдат попълнени');
      return;
    }

    try {
      const webhook = await createMutation.mutateAsync({
        name: newName,
        slug: newSlug,
        targetChannelId: newChannelId,
        botUserId: newBotUserId,
        messageTemplate: newTemplate || undefined,
      });
      setCreatedSecret(webhook.secret || null);
      toast.success('Уебхук е създаден');
    } catch (error) {
      toast.error(asApiError(error).message);
    }
  };

  const handleDelete = async (webhook: WebhookEndpoint) => {
    if (!confirm(`Изтриване на уебхук "${webhook.name}"?`)) return;
    try {
      await deleteMutation.mutateAsync(webhook.id);
      toast.success('Уебхукът е изтрит');
    } catch (error) {
      toast.error(asApiError(error).message);
    }
  };

  const handleToggle = async (webhook: WebhookEndpoint) => {
    try {
      await updateMutation.mutateAsync({
        webhookId: webhook.id,
        data: { enabled: !webhook.enabled },
      });
      toast.success(webhook.enabled ? 'Деактивиран' : 'Активиран');
    } catch (error) {
      toast.error(asApiError(error).message);
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const resetCreateForm = () => {
    setShowCreate(false);
    setNewName('');
    setNewSlug('');
    setNewChannelId('');
    setNewBotUserId('');
    setNewTemplate('');
    setCreatedSecret(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500 dark:text-dark-text-muted">
          Входящи уебхукове за получаване на съобщения от външни услуги.
        </p>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Нов уебхук
        </button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border-primary p-4 space-y-3">
          {createdSecret ? (
            <div className="space-y-3">
              <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <p className="text-sm font-medium text-green-800 dark:text-green-400 mb-1">
                  Уебхукът е създаден! Запазете тайната — показва се само веднъж.
                </p>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-green-100 dark:bg-green-900/40 px-2 py-1 rounded break-all">
                    {createdSecret}
                  </code>
                  <button
                    onClick={() => copyToClipboard(createdSecret, 'secret')}
                    className="text-green-600 hover:text-green-800"
                  >
                    {copiedId === 'secret' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <button onClick={resetCreateForm} className="text-sm text-blue-600 hover:text-blue-800">
                Затвори
              </button>
            </div>
          ) : (
            <form onSubmit={handleCreate} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-dark-text-muted mb-1">Име</label>
                  <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="GitHub Notifications" className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-sm" required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-dark-text-muted mb-1">Slug (URL)</label>
                  <input type="text" value={newSlug} onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))} placeholder="github" className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-sm font-mono" required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-dark-text-muted mb-1">Канал</label>
                  <select value={newChannelId} onChange={(e) => setNewChannelId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-sm" required>
                    <option value="">Изберете канал...</option>
                    {channels?.map((ch) => <option key={ch.id} value={ch.id}>#{ch.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-dark-text-muted mb-1">Бот (публикува като)</label>
                  <select value={newBotUserId} onChange={(e) => setNewBotUserId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-sm" required>
                    <option value="">Изберете бот...</option>
                    {bots?.map((b) => <option key={b.id} value={b.user_id}>@{b.slug}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-dark-text-muted mb-1">
                  Шаблон за съобщение <span className="text-gray-400">(Handlebars, по избор)</span>
                </label>
                <input type="text" value={newTemplate} onChange={(e) => setNewTemplate(e.target.value)} placeholder='{{title}}: {{message}}' className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-lg bg-white dark:bg-dark-bg-input text-sm font-mono" />
              </div>
              <div className="flex gap-2">
                <button type="submit" disabled={createMutation.isPending} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50 flex items-center gap-2">
                  {createMutation.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                  Създай
                </button>
                <button type="button" onClick={resetCreateForm} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                  Отказ
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Webhooks List */}
      {webhooks && webhooks.length > 0 ? (
        <div className="space-y-3">
          {webhooks.map((webhook) => (
            <div key={webhook.id} className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border-primary overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Webhook className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary truncate">{webhook.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <code className="text-xs text-gray-400 font-mono truncate">
                        /api/webhooks/{webhook.slug}
                      </code>
                      <button
                        onClick={() => copyToClipboard(`${API_BASE}/api/webhooks/${webhook.slug}`, webhook.id)}
                        className="text-gray-300 hover:text-gray-500"
                      >
                        {copiedId === webhook.id ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">
                    → #{webhook.channels?.name}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    webhook.enabled
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-gray-100 text-gray-600 dark:bg-dark-bg-input dark:text-dark-text-muted'
                  }`}>
                    {webhook.enabled ? 'Активен' : 'Неактивен'}
                  </span>
                  <div className="flex gap-1">
                    <button onClick={() => handleToggle(webhook)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded">
                      {webhook.enabled ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => setExpandedLogs(expandedLogs === webhook.id ? null : webhook.id)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                    >
                      {expandedLogs === webhook.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    <button onClick={() => handleDelete(webhook)} className="p-1.5 text-gray-400 hover:text-red-500 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Logs */}
              {expandedLogs === webhook.id && <WebhookLogsPanel webhookId={webhook.id} />}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-400 dark:text-dark-text-muted">
          <Webhook className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Няма създадени уебхукове</p>
        </div>
      )}
    </div>
  );
};

const WebhookLogsPanel = ({ webhookId }: { webhookId: string }) => {
  const { data: logs, isLoading } = useWebhookLogs(webhookId, 20);

  if (isLoading) {
    return <div className="px-4 py-3 border-t border-gray-100 dark:border-dark-border-primary text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Зареждане...</div>;
  }

  if (!logs || logs.length === 0) {
    return <div className="px-4 py-3 border-t border-gray-100 dark:border-dark-border-primary text-sm text-gray-400">Няма записани заявки</div>;
  }

  return (
    <div className="border-t border-gray-100 dark:border-dark-border-primary">
      <div className="max-h-48 overflow-y-auto">
        {logs.map((log) => (
          <div key={log.id} className="flex items-center gap-3 px-4 py-2 text-xs border-b border-gray-50 dark:border-dark-border-primary last:border-0">
            <span className={`w-8 text-center font-mono ${
              log.status_code && log.status_code < 400 ? 'text-green-600' : 'text-red-500'
            }`}>
              {log.status_code || '—'}
            </span>
            <span className="text-gray-400 w-16">{log.duration_ms ? `${log.duration_ms}ms` : '—'}</span>
            <span className="text-gray-400 flex-1 truncate">{log.error || 'OK'}</span>
            <span className="text-gray-300">{new Date(log.created_at).toLocaleString('bg-BG')}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
